import React, { useEffect, useState } from "react";
import { Select, DatePicker } from "antd";
import { withTranslation } from "react-i18next";
import { CalendarOutlined } from "@ant-design/icons";
import moment from "moment-timezone";

import { SingleSelect } from "../customComponent/CustomSelect";
import { ALL, THIS_WEEK, THIS_MONTH, NEXT_WEEK, NEXT_MONTH, LAST_WEEK, LAST_MONTH } from "../../constant/filters";
import { DATE_FORMAT_2 } from "../../utils/date";

const { RangePicker } = DatePicker;
const { Option } = Select;
const DATE_INPUT_SIZE = "small";

const DateRangeFilterComponent = ({
  t,
  timeRange,
  disabled,
  onSelect,
  dataTMId,
  options,
  onClear,
  showSearch,
  placeholder,
  defaultValue,
  customRangeId,
  ...props
}) => {
  const formatDate = (date, format) => (date ? moment(date, DATE_FORMAT_2).format(format) : "");

  const formattedStart = timeRange?.toDate ? formatDate(timeRange.fromDate, DATE_FORMAT_2) : timeRange?.fromDate;
  const formattedEnd = timeRange?.toDate ? formatDate(timeRange.toDate, DATE_FORMAT_2) : timeRange?.toDate;
  const dateRangeValue = timeRange?.toDate ? `${formattedStart} - ${formattedEnd}` : timeRange?.fromDate;

  const RangePickerId = customRangeId || undefined;

  const [isRangePickerOpen, setIsRangePickerOpen] = useState(false);
  const [rangePickerValues, setRangePickerValues] = useState([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isFocus, setIsFocus] = useState(false);

  const selectDateRange = (value) => {
    onSelect(value, null);
    setIsSelectOpen(false);
    setRangePickerValues([]);
  };

  useEffect(() => {
    if (timeRange?.fromDate && timeRange?.toDate) {
      const fromDate = moment(timeRange.fromDate, DATE_FORMAT_2);
      const toDate = moment(timeRange.toDate, DATE_FORMAT_2);
      setRangePickerValues([fromDate, toDate]);
      return;
    }

    setRangePickerValues([]);
  }, [timeRange]);

  const setRangePicker = (values) => {
    setIsRangePickerOpen(false);

    const fromDate = values[0] ? moment(values[0]).format(DATE_FORMAT_2) : null;
    const toDate = values[1] ? moment(values[1]).format(DATE_FORMAT_2) : null;

    onSelect(fromDate, toDate);

    setRangePickerValues(values);

    setIsSelectOpen(false);
  };

  const setDatesPick = (val) => {
    const selectedDates = {};
    selectedDates.fromDate = val[0] ?? null;
    selectedDates.toDate = val[1] ?? null;
  };

  return (
    <SingleSelect
      dataTMId={dataTMId}
      disabled={disabled}
      changed={timeRange && timeRange?.fromDate !== ALL}
      value={dateRangeValue || defaultValue}
      open={isSelectOpen}
      allowClear={true}
      showSearch={showSearch}
      onClear={onClear}
      placeholder={placeholder}
      onClick={() => {
        if (isFocus && !isSelectOpen) {
          setIsSelectOpen(true);
        }
      }}
      onFocus={() => {
        setIsFocus(true);
        setIsSelectOpen(true);
      }}
      onBlur={() => {
        setIsFocus(false);
        if (!isRangePickerOpen) {
          setIsSelectOpen(false);
        }
      }}
      onSelect={(value) => {
        setIsSelectOpen(false);
        selectDateRange(value);
      }}
      suffixIcon={<CalendarOutlined />}
      dropdownRender={(menu) => (
        <div>
          {menu}
          <div id={RangePickerId}>
            <RangePicker
              value={rangePickerValues}
              suffixIcon={null}
              clearIcon={null}
              separator={null}
              bordered={false}
              allowEmpty={[false, false]}
              size={DATE_INPUT_SIZE}
              open={isRangePickerOpen}
              onOpenChange={(open) => {
                setIsRangePickerOpen(open);
                !open && setIsSelectOpen(false);
              }}
              onChange={setRangePicker}
              onCalendarChange={setDatesPick}
            />
          </div>
        </div>
      )}
      {...props}
    >
      {options ? (
        options.map((opt, index) => {
          // Handle both format (string) and object format (object with value and id)
          const value = typeof opt === "string" ? opt : opt.value;
          const id = typeof opt === "string" ? undefined : opt.id;
          return (
            <Option key={index} value={value} id={id}>
              {t(value)}
            </Option>
          );
        })
      ) : (
        <>
          <Option value={ALL} id="date-range-filter_all-times">
            {t("all_times")}
          </Option>
          <Option value={LAST_WEEK} id="date-range-filter_last-week">
            {t("last_week")}
          </Option>
          <Option value={LAST_MONTH} id="date-range-filter_last-month">
            {t("last_month")}
          </Option>
          <Option value={THIS_WEEK} id="date-range-filter_this-week">
            {t("this_week")}
          </Option>
          <Option value={THIS_MONTH} id="date-range-filter_this-month">
            {t("this_month")}
          </Option>
          <Option value={NEXT_WEEK} id="date-range-filter_next-week">
            {t("next_week")}
          </Option>
          <Option value={NEXT_MONTH} id="date-range-filter_next-month">
            {t("next_month")}
          </Option>
        </>
      )}
    </SingleSelect>
  );
};
const DateRangeFilter = withTranslation()(DateRangeFilterComponent);

export { DateRangeFilter };
